// @ts-nocheck
import formatDate from "includes/formatDate";
import genRequest from "includes/request";
import generateUUID from "includes/UUID";
import FormSectionModel from "./FormSectionModel";

class FormObject {
	#formId = null;
	#employeeId = null;
	#title = null;
	#txt = null;
	#publishedDate = null;
	#savePublishedDate = null;
	#availableToDate = null;
	#isDraft = true;
	#isAnonymous = true;
	#isAvailable = true;
	#answerVisibleForUser = true;
	#lastChanged = null;
	#lastChangeAuthorId = null;

	#sections = [];
	#questions = [];
	#linkTo = [];
	#companyId = false;
	#hasAnswers = false;

	constructor(obj) {
		let basic_form = BasicForm();
		let date = formatDate();

		this.#formId = obj?.id || obj?.formId || basic_form.formId || null;
		this.#employeeId = obj?.employeeId
			? obj.employeeId
			: basic_form.employeeId;
		this.#title = obj?.title ? obj.title : basic_form.title;
		this.#txt = obj?.txt ? obj.txt : basic_form.txt;
		this.#savePublishedDate = date;
		this.#publishedDate = obj?.publishedDate ? obj.publishedDate : date;
		this.#availableToDate = obj?.availableToDate
			? obj.availableToDate
			: null;
		this.#isDraft =
			typeof obj?.isDraft !== "undefined" ? obj?.isDraft : true;
		this.#isAnonymous =
			typeof obj?.isAnonymous !== "undefined" ? obj?.isAnonymous : true;
		this.#isAvailable = obj?.isAvailable ? obj?.isAvailable : true;
		this.#answerVisibleForUser = obj?.answerVisibleForUser
			? obj?.answerVisibleForUser
			: true;
		this.#lastChanged = obj?.lastChanged
			? obj.lastChanged
			: basic_form.lastChanged;
		this.#lastChangeAuthorId = obj?.lastChangeAuthorId
			? obj.lastChangeAuthorId
			: basic_form.lastChangeAuthorId;
		this.#companyId = obj?.companyId ? obj.companyId : basic_form.companyId;
		this.#hasAnswers = obj?.hasAnswers ? obj.hasAnswers : false;

		if (obj?.sections)
			this.#sections = obj.sections.map((a) => {
				return new FormSectionModel(a, this);
			});
		else
			this.#sections = basic_form.sections.map((a) => {
				return new FormSectionModel(a, this);
			});
		this.#linkTo = [];
	}

	fetchForEdit() {
		let proms = [this.fetchAssignations()];
		return Promise.all(proms);
	}

	fetchAssignations() {
		let req = genRequest(
			`forms/${this.#formId}/affectations`
		).then((resp) => {
			this.#linkTo = resp?.length ? resp : [];
			return this.#linkTo;
		});
		return req;
	}

	send(account) {
		let that = this;
		const isAnUpdate = this.#formId ? true : false;

		let prom = new Promise((resolve, reject) => {
			this.sendForm()
				.then(() => {
					return that.linkForm();
				}, reject)
				.then(() => {
					that.sendSections(true);
				}, reject)
				.then(() => {
					that.sendNotifications(account, isAnUpdate);
				}, reject)
				.then(() => {
					resolve(true);
				}, reject);
		});
		return prom;
	}

	sendNotifications(account, isAnUpdate) {
		if (!isAnUpdate) {
			// Recipients are resolved by the API from the form's affectations.
			if (this.#savePublishedDate === this.#publishedDate) {
				return genRequest(
					`forms/${this.#formId}/notify`,
					{},
					"post"
				);
			} else {
				return genRequest(
					`forms/${this.#formId}/notify-delay`,
					{ timeToNotify: this.#publishedDate },
					"post"
				);
			}
		}
		return true;
	}

	sendForm() {
		let that = this;
		let values = this.values();
		if (!values.formId) delete values.formId;
		let req = genRequest(
			"forms/" + (this.#formId ? "raw/" + this.#formId : "raw"),
			values,
			this.#formId ? "put" : "post"
		).then((resp) => {
			if (!that.#formId) that.id(resp.id || resp.formId);
			return resp;
		});
		return req;
	}

	linkForm() {
		let links = this.#linkTo;
		if (!links?.length)
			links = [
				{
					employeeId: this.#employeeId,
					companyId: this.#companyId,
				},
			];
		let affects = links.map((a) => ({
			...a,
			formId: this.#formId,
		}));
		let req = genRequest(
			`forms/${this.#formId}/affectations`,
			affects,
			"post"
		);
		return req;
	}

	affectations(affects) {
		if (typeof affects !== "undefined") this.#linkTo = affects;
		return this.#linkTo;
	}

	sendSections() {
		let proms = [];

		for (let sec of this.#sections) proms.push(sec.send());
		return Promise.all(proms);
	}

	sections(sec_id, with_index) {
		if (!sec_id) return this.#sections;
		for (let [key, sec] of this.#sections) {
			if (sec.id === sec_id) {
				if (with_index)
					return {
						index: parseInt(key),
						question: sec,
					};
				else return sec;
			}
		}
	}

	curSection(new_sec, from_quest) {
		if (new_sec) {
			if (new_sec !== this.cur_section) {
				this.cur_section = new_sec;
				if (!from_quest) this.curQuestion(false, true);
			}
		} else if (new_sec === false) {
			this.cur_section = false;
			this.cur_question = false;
		}
		return this.cur_section;
	}

	addSection(index) {
		let default_obj = {
			position: this.#sections.length,
			tempId: generateUUID(),
			title: "",
			txt: "",
			formId: this.#formId,
		};
		if (index) default_obj.position = index;
		let sec_obj = new FormSectionModel(default_obj, this);
		if (!index) this.#sections.push(sec_obj);
		else this.#sections.splice(index, 0, sec_obj);
		this.reorderSections();
		return sec_obj;
	}

	removeSection(secId) {
		const idx = this.#sections.findIndex((s) => s.id === secId);
		if (idx !== -1) {
			let deleted = this.#sections.splice(idx, 1);
			if (this.#sections.length) this.curSection(this.#sections[0]);
			else this.curSection(false);
			this.reorderSections();
			return deleted;
		}
		return false;
	}

	reorderSections() {
		let pos = 0;
		for (let sec of this.#sections) sec.position = pos++;
	}

	questions(quest_id, with_index) {
		if (!quest_id)
			return this.#sections.map((a) => {
				return a.questions();
			});
		for (let [key, quest] of this.#questions) {
			if (quest.id === quest_id) {
				if (with_index)
					return {
						index: parseInt(key),
						question: quest,
					};
				else return quest;
			}
		}
	}

	curQuestion(new_quest, from_sec) {
		if (
			(new_quest && new_quest !== this.cur_question) ||
			new_quest === false
		) {
			this.cur_question = new_quest;
			if (!from_sec && new_quest) this.curSection(new_quest.parent, true);
		}
		return this.cur_question;
	}

	checkValues() {
		if (!this.#sections.length)
			return {
				section: null,
				error: "FORM_NO_SECTION_ERROR",
			};
		this.#title = this.#sections[0].title;
		this.#txt = this.#sections[0].txt;
		if (!this.#title?.length)
			return {
				section: null,
				selector:
					"[data-form-section-id='" +
					this.#sections[0].id +
					"'] .section-title",
				error: "FORM_EMPTY_TITLE",
			};
		for (let sec of this.#sections) {
			let check = sec.checkValues();
			if (check !== true)
				return {
					section: sec,
					...check,
				};
		}
		return true;
	}

	title = () => this.#title;

	id(value, noTemp) {
		if (value) {
			this.#formId = value;
			for (let sec of this.#sections) sec.setFormId(value);
		}
		return this.#formId
			? this.#formId
			: noTemp !== true
			? this.TempId
			: false;
	}

	lastChanged = () => this.#lastChanged;

	isDraft(set_value) {
		if (typeof set_value !== "undefined") this.#isDraft = set_value;
		return this.#isDraft;
	}

	isAnonymous(set_value) {
		if (typeof set_value !== "undefined") this.#isAnonymous = set_value;
		return this.#isAnonymous;
	}

	isAvailable(set_value) {
		if (typeof set_value !== "undefined") this.#isAvailable = set_value;
		return this.#isAvailable;
	}

	publishedDate(set_value) {
		if (typeof set_value !== "undefined") this.#publishedDate = set_value;
		return this.#publishedDate;
	}

	availableToDate(set_value) {
		if (typeof set_value !== "undefined") this.#availableToDate = set_value;
		return this.#availableToDate;
	}

	hasAnswers = () => this.#hasAnswers;

	values = () => {
		return {
			formId: this.#formId,
			employeeId: this.#employeeId,
			title: this.#title,
			txt: this.#txt,
			publishedDate: this.#publishedDate,
			availableToDate: this.#availableToDate,
			isDraft: this.#isDraft,
			isAnonymous: this.#isAnonymous,
			isAvailable: this.#isAvailable,
			lastChanged: this.#lastChanged,
			answerVisibleForUser: this.#answerVisibleForUser,
			lastChangeAuthorId: this.#lastChangeAuthorId,
		};
	};
}

export function BasicForm() {
	let date = formatDate();

	return {
		lastChanged: date,
		availableToDate: date,
		publishedDate: date,
		tempId: generateUUID(),
		title: "",
		txt: "",
		sections: [
			{
				position: 0,
				tempId: generateUUID(),
				title: "",
				txt: "",
				questions: [
					{
						tempId: generateUUID(),
						title: "",
						txt: "",
						questionDesignId: 1,
					},
				],
			},
		],
	};
}

export default FormObject;
